import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../components/Modal';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { useBillableNavigationLock } from '../hooks/useBillableNavigationLock';
import { ordersApi, trackingApi, shipmentsApi } from '../api/client';
import {
  getEffectiveRefundStatus,
  getRefundUpdateTimestamp,
  mergeFilteredRecentOrders,
  updateRecentOrderStatus,
} from '../utils/recentOrders';
import { shouldApplyLocalRefundOverride } from '../utils/refunds';
import { buildPrintPath } from '../utils/labels';
import type { OrderHistoryEntry, TrackingInfo, DateFilters } from '../types';

interface LocalOrderOverride {
  status?: {
    at: string;
    patch: Partial<OrderHistoryEntry>;
  };
  tracking?: {
    at: string;
    patch: Partial<OrderHistoryEntry>;
  };
}

function timestamp(value?: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function safeExternalTrackingUrl(value?: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function OrderHistoryPage() {
  const { toasts, removeToast, success, error, warning, info } = useToast();

  const [orders, setOrders] = useState<OrderHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filters, setFilters] = useState<DateFilters>({});
  const ordersRequestRef = useRef(0);
  const loadMoreRequestRef = useRef(0);
  const trackingRequestRef = useRef(0);
  const loadedAdditionalPagesRef = useRef(false);
  const filterRevisionRef = useRef(0);
  const localOrderOverridesRef = useRef(new Map<string, LocalOrderOverride>());

  // Tracking modal state
  const [trackingModal, setTrackingModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    order: OrderHistoryEntry | null;
    tracking: TrackingInfo | null;
  }>({
    isOpen: false,
    loading: false,
    order: null,
    tracking: null,
  });

  // Cancel modal state
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    order: OrderHistoryEntry | null;
  }>({
    isOpen: false,
    loading: false,
    order: null,
  });

  // Syncing state
  const [syncing, setSyncing] = useState(false);
  const [trackingSyncCursor, setTrackingSyncCursor] = useState<string | null>(null);

  useBillableNavigationLock(cancelModal.loading);

  const mergeOrderPages = useCallback((primary: OrderHistoryEntry[], secondary: OrderHistoryEntry[]) => {
    const seen = new Set(primary.map((order) => order.shipment_id || order.record_key));
    return [
      ...primary,
      ...secondary.filter((order) => {
        const identity = order.shipment_id || order.record_key;
        if (seen.has(identity)) return false;
        seen.add(identity);
        return true;
      }),
    ];
  }, []);

  const applyLocalOrderOverrides = useCallback((entries: OrderHistoryEntry[]) => {
    return entries.map((entry) => {
      const override = localOrderOverridesRef.current.get(entry.shipment_id);
      if (!override) return entry;

      let merged = entry;
      let keepStatus = override.status;
      let keepTracking = override.tracking;

      if (keepStatus) {
        const localCandidate = { ...entry, ...keepStatus.patch };
        if (shouldApplyLocalRefundOverride(entry, localCandidate)) {
          merged = { ...merged, ...keepStatus.patch };
        } else {
          keepStatus = undefined;
        }
      }

      if (keepTracking) {
        if (timestamp(entry.tracking_updated_at) >= timestamp(keepTracking.at)) {
          keepTracking = undefined;
        } else {
          merged = { ...merged, ...keepTracking.patch };
        }
      }

      if (keepStatus || keepTracking) {
        localOrderOverridesRef.current.set(entry.shipment_id, {
          status: keepStatus,
          tracking: keepTracking,
        });
      } else {
        localOrderOverridesRef.current.delete(entry.shipment_id);
      }

      return merged;
    });
  }, []);

  const loadOrders = useCallback(async (silent = false) => {
    const requestId = ++ordersRequestRef.current;
    loadMoreRequestRef.current += 1;
    if (!silent) loadedAdditionalPagesRef.current = false;
    setLoadingMore(false);
    // Keep the current paging position during a silent refresh until the
    // replacement first page succeeds. A transient refresh failure should not
    // make the already-visible "Load More" action disappear.
    if (!silent) setNextCursor(null);
    if (!silent) setLoading(true);
    try {
      const page = await ordersApi.list(filters, { limit: 200 });
      if (requestId !== ordersRequestRef.current) return;
      // Merge with recent orders from sessionStorage to provide immediate visibility
      // This works around KV eventual consistency for list() operations
      const firstPage = mergeFilteredRecentOrders(
        applyLocalOrderOverrides(page.orders),
        filters
      );
      setOrders((current) => silent ? mergeOrderPages(firstPage, current) : firstPage);
      // A silent refresh retains already-loaded older pages, so it must also
      // retain their continuation cursor. Resetting to page one's cursor would
      // make subsequent clicks refetch duplicate pages before progressing.
      if (!silent || !loadedAdditionalPagesRef.current) {
        setNextCursor(page.next_cursor);
      }
    } catch (err) {
      if (!silent && requestId === ordersRequestRef.current) {
        const message = err instanceof Error ? err.message : 'Failed to load orders';
        error('Load Failed', message);
      }
    } finally {
      if (requestId === ordersRequestRef.current) setLoading(false);
    }
  }, [filters, error, mergeOrderPages, applyLocalOrderOverrides]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;

    const ordersRequestId = ordersRequestRef.current;
    const loadMoreRequestId = ++loadMoreRequestRef.current;
    const cursor = nextCursor;
    setLoadingMore(true);

    try {
      const page = await ordersApi.list(filters, { limit: 200, cursor });
      if (
        ordersRequestId !== ordersRequestRef.current ||
        loadMoreRequestId !== loadMoreRequestRef.current
      ) return;

      const olderPage = mergeFilteredRecentOrders(
        applyLocalOrderOverrides(page.orders),
        filters
      );
      setOrders((current) => mergeOrderPages(current, olderPage));
      loadedAdditionalPagesRef.current = true;
      setNextCursor(page.next_cursor);
    } catch (err) {
      if (
        ordersRequestId === ordersRequestRef.current &&
        loadMoreRequestId === loadMoreRequestRef.current
      ) {
        const message = err instanceof Error ? err.message : 'Failed to load older orders';
        error('Load More Failed', message);
      }
    } finally {
      if (
        ordersRequestId === ordersRequestRef.current &&
        loadMoreRequestId === loadMoreRequestRef.current
      ) setLoadingMore(false);
    }
  };

  // Initial load and filter changes
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => () => {
    ordersRequestRef.current += 1;
    loadMoreRequestRef.current += 1;
    trackingRequestRef.current += 1;
  }, []);

  // Auto-refresh every 60 seconds (only when page is visible)
  useEffect(() => {
    const REFRESH_INTERVAL = 60000; // 60 seconds

    const refreshIfVisible = () => {
      if (!document.hidden) {
        loadOrders(true); // Silent refresh
      }
    };

    const interval = setInterval(refreshIfVisible, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [loadOrders]);

  const handleFilterChange = (field: keyof DateFilters, value: string) => {
    filterRevisionRef.current += 1;
    ordersRequestRef.current += 1;
    loadMoreRequestRef.current += 1;
    setNextCursor(null);
    setLoadingMore(false);
    loadedAdditionalPagesRef.current = false;
    setFilters((prev) => {
      const next = { ...prev, [field]: value || undefined };
      if (field === 'date' && value) {
        next.startDate = undefined;
        next.endDate = undefined;
      } else if ((field === 'startDate' || field === 'endDate') && value) {
        next.date = undefined;
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    filterRevisionRef.current += 1;
    ordersRequestRef.current += 1;
    loadMoreRequestRef.current += 1;
    setNextCursor(null);
    setLoadingMore(false);
    loadedAdditionalPagesRef.current = false;
    setFilters({});
  };

  const handleViewTracking = async (order: OrderHistoryEntry) => {
    if (!order.tracking_code) {
      error('No Tracking', 'This shipment does not have tracking information.');
      return;
    }

    const requestId = ++trackingRequestRef.current;
    setTrackingModal({
      isOpen: true,
      loading: true,
      order,
      tracking: null,
    });

    try {
      const tracking = await trackingApi.getByShipment(order.shipment_id);
      if (requestId !== trackingRequestRef.current) return;
      setTrackingModal((prev) => ({
        ...prev,
        loading: false,
        tracking,
      }));

      // Sync tracking status to backend and update local state
      trackingApi.syncOne(order.shipment_id).catch(() => {
        // Silently ignore sync errors - the modal still shows correct data
      });

      const trackingUpdatedAt = tracking.updated_at || new Date().toISOString();
      const existingOverride = localOrderOverridesRef.current.get(order.shipment_id) || {};
      const trackingPatch: Partial<OrderHistoryEntry> = {
        tracking_status: tracking.status,
        tracking_status_label: tracking.status_label,
        tracking_status_tone: tracking.status_tone,
        tracking_updated_at: trackingUpdatedAt,
        est_delivery_date: tracking.est_delivery_date,
      };
      localOrderOverridesRef.current.set(order.shipment_id, {
        ...existingOverride,
        tracking: { at: trackingUpdatedAt, patch: trackingPatch },
      });

      // Invalidate an older in-flight first-page refresh before applying this
      // direct EasyPost result.
      ordersRequestRef.current += 1;

      // Update local orders state with fresh tracking info
      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.shipment_id === order.shipment_id
            ? {
                ...o,
                ...trackingPatch,
              }
            : o
        )
      );
    } catch (err) {
      if (requestId !== trackingRequestRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load tracking';
      error('Tracking Error', message);
      setTrackingModal((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const closeTrackingModal = () => {
    trackingRequestRef.current += 1;
    setTrackingModal({ isOpen: false, loading: false, order: null, tracking: null });
  };

  const handleSyncAll = async () => {
    const filterRevision = filterRevisionRef.current;
    setSyncing(true);
    try {
      const result = await trackingApi.syncAll({
        force: true,
        limit: 100,
        cursor: trackingSyncCursor || undefined,
      });
      setTrackingSyncCursor(result.next_cursor);
      const continuation = result.list_complete
        ? ''
        : ' More shipment history remains; select Continue Tracking Sync to process the next page.';
      if (result.attempted === 0) {
        info(
          result.list_complete ? 'Nothing to Sync' : 'Sync Page Complete',
          `${result.skipped} shipments were not eligible for an update.${continuation}`
        );
      } else if (result.failed >= result.attempted) {
        error(
          'Sync Failed',
          `Tracking could not be updated for ${result.failed} shipments.${continuation}`
        );
      } else if (result.failed > 0) {
        warning(
          'Sync Partially Complete',
          `${result.updated} updated, ${result.failed} failed, and ${result.skipped} skipped.${continuation}`
        );
      } else {
        success(
          result.list_complete ? 'Sync Complete' : 'Sync Page Complete',
          `Updated tracking for ${result.updated} shipments.${continuation}`
        );
      }
      if (filterRevision === filterRevisionRef.current) {
        await loadOrders();
      }
    } catch (err) {
      if ([400, 409].includes((err as { status?: number })?.status ?? 0)) {
        setTrackingSyncCursor(null);
      }
      const message = err instanceof Error ? err.message : 'Failed to sync tracking';
      error('Sync Failed', message);
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelShipment = async () => {
    const order = cancelModal.order;
    if (!order) return;

    if (!order.shipment_id) {
      error('Cancel Failed', 'A shipment ID is required to request a cancellation.');
      setCancelModal({ isOpen: false, loading: false, order: null });
      return;
    }

    setCancelModal((prev) => ({ ...prev, loading: true }));
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);

    try {
      const cancellation = await shipmentsApi.cancel(order.shipment_id, order.record_key, controller.signal);
      const returnedEntry = cancellation.entry;
      const responseState: OrderHistoryEntry = {
        ...order,
        ...(returnedEntry || {}),
        refund_status: returnedEntry?.refund_status || cancellation.refund_status || 'submitted',
      };
      const nextRefundStatus = getEffectiveRefundStatus(responseState) || 'submitted';
      const statusUpdatedAt = getRefundUpdateTimestamp(responseState) || new Date().toISOString();
      const statusPatch: Partial<OrderHistoryEntry> = {
        ...(returnedEntry || {}),
        status: returnedEntry?.status || order.status,
        refund_status: nextRefundStatus,
        status_updated_at: statusUpdatedAt,
        refund_updated_at: returnedEntry?.refund_updated_at || statusUpdatedAt,
        cancelled_at: returnedEntry?.cancelled_at || statusUpdatedAt,
      };
      const existingOverride = localOrderOverridesRef.current.get(order.shipment_id) || {};
      localOrderOverridesRef.current.set(order.shipment_id, {
        ...existingOverride,
        status: { at: statusUpdatedAt, patch: statusPatch },
      });
      ordersRequestRef.current += 1;
      setOrders((current) => current.map((entry) =>
        entry.shipment_id === order.shipment_id
          ? { ...entry, ...statusPatch }
          : entry
      ));
      updateRecentOrderStatus(order.shipment_id, nextRefundStatus, statusUpdatedAt, statusPatch);
      if (nextRefundStatus === 'refunded') {
        success('Shipment Refunded', 'EasyPost reports that the refund is complete.');
      } else if (nextRefundStatus === 'rejected') {
        error('Refund Rejected', 'EasyPost rejected the refund request.');
      } else if (nextRefundStatus === 'cancelled') {
        info('Shipment Cancelled', 'EasyPost reports that the shipment is cancelled.');
      } else {
        info('Cancellation Requested', 'The refund request has been submitted.');
      }
      setCancelModal({ isOpen: false, loading: false, order: null });
    } catch (err) {
      const message = controller.signal.aborted
        ? 'The refund request timed out. Retry the same shipment to recover its authoritative refund state safely.'
        : err instanceof Error ? err.message : 'Failed to cancel shipment';
      error('Cancel Failed', message);
      setCancelModal((prev) => ({ ...prev, loading: false }));
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? dateStr : date.toLocaleString();
  };

  const getRefundStatus = (order: OrderHistoryEntry) => {
    return getEffectiveRefundStatus(order);
  };

  const getStatusBadgeClass = (order: OrderHistoryEntry) => {
    const refundStatus = getRefundStatus(order);
    if (refundStatus === 'submitted') return 'warning';
    if (refundStatus === 'refunded') return 'success';
    if (refundStatus === 'rejected') return 'error';
    if (refundStatus === 'cancelled') return 'cancelled';

    const status = order.tracking_status || order.status;
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'delivered';
      case 'in_transit':
      case 'out_for_delivery':
        return 'in-transit';
      case 'cancelled':
      case 'refunded':
        return 'cancelled';
      case 'error':
      case 'failure':
      case 'purchase_failed':
        return 'error';
      case 'purchase_pending':
        return 'warning';
      default:
        return 'purchased';
    }
  };

  const getStatusLabel = (order: OrderHistoryEntry) => {
    const refundStatus = getRefundStatus(order);
    if (refundStatus === 'submitted') return 'Refund Submitted';
    if (refundStatus === 'refunded') return 'Refunded';
    if (refundStatus === 'rejected') return 'Refund Rejected';
    if (refundStatus === 'cancelled') return 'Cancelled';

    if (order.tracking_status_label) return order.tracking_status_label;
    if (order.tracking_status) {
      return order.tracking_status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return order.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Filter Form */}
      <div
        className="panel"
        style={{ marginBottom: '24px', padding: '18px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '1.1em' }}>Filter Orders</h2>
          <button
            className={`btn btn-primary ${syncing ? 'loading' : ''}`}
            onClick={handleSyncAll}
            disabled={syncing}
            style={{ padding: '10px 20px' }}
          >
            {syncing
              ? 'Syncing...'
              : trackingSyncCursor
                ? 'Continue Tracking Sync'
                : 'Sync Tracking'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor="filter-search">Search by Name</label>
            <input
              id="filter-search"
              type="text"
              placeholder="Search recipient..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor="filter-date">Specific Date</label>
            <input
              id="filter-date"
              type="date"
              value={filters.date || ''}
              onChange={(e) => handleFilterChange('date', e.target.value)}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor="filter-start">Start Date</label>
            <input
              id="filter-start"
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor="filter-end">End Date</label>
            <input
              id="filter-end"
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={handleClearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>
            Shipment History
            <span className="count-pill" style={{ marginLeft: '12px' }}>
              {orders.length}
            </span>
          </h2>
        </div>

        {loading ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            <div className="skeleton" style={{ height: '60px' }} />
            <div className="skeleton" style={{ height: '60px' }} />
            <div className="skeleton" style={{ height: '60px' }} />
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <p>No shipments found for the selected filters.</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Recipient</th>
                  <th>Destination</th>
                  <th>Tracking</th>
                  <th>Label</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.record_key || order.shipment_id}>
                    <td data-label="Date">{formatDate(order.created_at)}</td>
                    <td data-label="Recipient">{order.recipient || '-'}</td>
                    <td data-label="Destination">{order.destination || '-'}</td>
                    <td data-label="Tracking">
                      {order.tracking_code ? (
                        <code style={{ fontSize: '0.85em' }}>{order.tracking_code}</code>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td data-label="Label">
                      {order.label_url ? (
                        <Link to={buildPrintPath(order.label_url, order.label_file_type)}>
                          View Label
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge ${getStatusBadgeClass(order)}`}>
                        {getStatusLabel(order)}
                      </span>
                      {order.est_delivery_date && (
                        <div style={{ fontSize: '0.75em', color: 'var(--aged-mahogany-light)', marginTop: '4px' }}>
                          Est: {new Date(order.est_delivery_date).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td data-label="Actions">
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {order.tracking_code && (
                          <button
                            className="btn btn-primary"
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            onClick={() => handleViewTracking(order)}
                          >
                            Track
                          </button>
                        )}
                        {order.status === 'purchased' && !getRefundStatus(order) && (
                          <button
                            className="btn btn-danger"
                            style={{ padding: '6px 12px', fontSize: '0.85em' }}
                            onClick={() => setCancelModal({ isOpen: true, loading: false, order })}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            <div
              aria-live="polite"
              style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px' }}
            >
              {nextCursor ? (
                <button
                  className={`btn btn-secondary ${loadingMore ? 'loading' : ''}`}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading Older Shipments...' : 'Load More Shipments'}
                </button>
              ) : (
                <span style={{ color: 'var(--aged-mahogany-light)', fontSize: '0.9em' }}>
                  All matching shipments are shown.
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Tracking Modal */}
      <Modal
        isOpen={trackingModal.isOpen}
        onClose={closeTrackingModal}
        title="Tracking Details"
      >
        {trackingModal.loading ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            <div className="skeleton" style={{ height: '40px' }} />
            <div className="skeleton" style={{ height: '100px' }} />
          </div>
        ) : trackingModal.tracking ? (
          <div>
            <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
              <div>
                <strong>Tracking Number:</strong>
                <div style={{ fontFamily: 'monospace', marginTop: '4px' }}>
                  {trackingModal.tracking.tracking_code}
                </div>
              </div>
              <div>
                <strong>Carrier:</strong> {trackingModal.tracking.carrier}
              </div>
              <div>
                <strong>Status:</strong>{' '}
                <span className={`status-badge ${trackingModal.tracking.status_tone || 'info'}`}>
                  {trackingModal.tracking.status_label}
                </span>
              </div>
              {trackingModal.tracking.est_delivery_date && (
                <div>
                  <strong>Estimated Delivery:</strong>{' '}
                  {new Date(trackingModal.tracking.est_delivery_date).toLocaleDateString()}
                </div>
              )}
            </div>

            {trackingModal.tracking.tracking_details && trackingModal.tracking.tracking_details.length > 0 && (
              <div>
                <strong style={{ display: 'block', marginBottom: '12px' }}>Tracking History:</strong>
                <div
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    background: 'var(--champagne-dune)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '12px',
                  }}
                >
                  {trackingModal.tracking.tracking_details.map((detail, i) => {
                    const structuredLocation = detail.tracking_location
                      ? [
                          detail.tracking_location.city,
                          detail.tracking_location.state,
                          detail.tracking_location.zip,
                          detail.tracking_location.country,
                        ].filter(Boolean).join(', ')
                      : '';
                    const location = detail.location || structuredLocation;

                    return (
                      <div
                        key={`${detail.datetime}-${i}`}
                        style={{
                          padding: '12px 0',
                          borderBottom: i < trackingModal.tracking!.tracking_details.length - 1 ? '1px solid rgba(201, 168, 129, 0.3)' : 'none',
                        }}
                      >
                        <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)' }}>
                          {formatDate(detail.datetime)}
                        </div>
                        <div style={{ fontWeight: 500, marginTop: '4px' }}>{detail.message}</div>
                        {location && (
                          <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)', marginTop: '2px' }}>
                            {location}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {safeExternalTrackingUrl(
              trackingModal.tracking.public_url || trackingModal.tracking.tracking_url
            ) && (
              <div style={{ marginTop: '20px' }}>
                <a
                  href={safeExternalTrackingUrl(
                    trackingModal.tracking.public_url || trackingModal.tracking.tracking_url
                  ) || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  View on Carrier Website
                </a>
              </div>
            )}
          </div>
        ) : (
          <p>No tracking information available.</p>
        )}
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={cancelModal.isOpen}
        onClose={() => setCancelModal({ isOpen: false, loading: false, order: null })}
        title="Cancel Shipment"
        dismissible={!cancelModal.loading}
      >
        <p style={{ marginBottom: '20px' }}>
          Are you sure you want to request a cancellation/refund for this shipment?
        </p>
        {cancelModal.order && (
          <div
            style={{
              background: 'var(--champagne-dune)',
              padding: '16px',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '20px',
            }}
          >
            <div><strong>Recipient:</strong> {cancelModal.order.recipient}</div>
            <div><strong>Tracking:</strong> {cancelModal.order.tracking_code || '-'}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-ghost"
            onClick={() => setCancelModal({ isOpen: false, loading: false, order: null })}
            disabled={cancelModal.loading}
          >
            Keep Shipment
          </button>
          <button
            className={`btn btn-danger ${cancelModal.loading ? 'loading' : ''}`}
            onClick={handleCancelShipment}
            disabled={cancelModal.loading}
          >
            {cancelModal.loading ? 'Cancelling...' : 'Request Refund'}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default OrderHistoryPage;
